import { addDays } from './checkin-weeks';

export const STAGE_ROLES = [
  'contacted',
  'replied',
  'replied_no',
  'booked',
  'held',
  'decision',
  'won',
  'lost',
  'lead',
] as const;

export type StageRole = (typeof STAGE_ROLES)[number];

/** The ladder roles a sequence channel carries, once each and in this order. */
export const SEQUENCE_LADDER_ROLES: StageRole[] = [
  'contacted',
  'replied',
  'booked',
  'held',
  'decision',
  'won',
];

export const SEQUENCE_EXIT_ROLES: StageRole[] = ['replied_no', 'lost'];

export const MAX_CHANNELS = 4;
export const MAX_STEPS_STAGES = 8;
/** Channel families on a course where each participant maps their own channels. */
export const MAX_FAMILIES = 8;
/** A participant's own channels, each ending at their destination. */
export const MAX_FUNNELS = 4;
/** Stages of a family or a participant's channel, before the destination. */
export const MAX_FUNNEL_STAGES = 7;
export const MAX_SEQUENCE_STEPS = 8;
export const MAX_OPENING_STEPS = 3;
export const MAX_GAP_DAYS = 60;
export const MAX_LABEL_LENGTH = 40;
export const MAX_SHORT_LENGTH = 20;
export const MAX_TARGET_CENTS = 100000000;
/** Bets per participant: Danny's "two or three", and one so a participant can start early. */
export const MAX_BETS = 3;

export const KEY_PATTERN = /^[a-z0-9_]{1,48}$/;
const MAX_KEY_LENGTH = 48;

export type TrackerStage = { key: string; label: string; short?: string; role?: StageRole };

export type TrackerChannel = {
  key: string;
  label: string;
  /** 'sequence' is the RIS flow; 'steps' is an ordered list of dated steps (Leads). */
  flow: 'sequence' | 'steps';
  stages: TrackerStage[];
  exits: TrackerStage[];
};

/** `key` is stable: touches store it, so renaming a step keeps scheduling intact. */
export type TrackerSequenceStep = { key: string; name: string; gapDays: number };

/** Cost per lead reads each participant's own destination and target. */
export type TrackerScoreboard = { kind: 'revenue' } | { kind: 'cpl' };

/**
 * Who maps the channels: the course (RIS), or each participant, copying one
 * of the course's channel families and naming its stages (Leads).
 */
export type TrackerChannelsBy = 'course' | 'participant';

/**
 * A participant's own channel, copied from a family: its stages before the
 * destination, which is always the last stage and is not stored.
 */
export type TrackerFunnel = {
  key: string;
  familyKey: string;
  label: string;
  stages: TrackerStage[];
};

export type TrackerFields = {
  offer: boolean;
  email: boolean;
  phone: boolean;
  segment: boolean;
  tier: boolean;
  note: boolean;
};

export type TrackerConfig = {
  version: 1;
  channels: TrackerChannel[];
  rollupLabels: { sends: string; replies: string; held: string; closes: string };
  sequence: TrackerSequenceStep[];
  scoreboard: TrackerScoreboard;
  fields: TrackerFields;
  /**
   * Participants list 1–3 bets (an offer aimed at one audience) and put each
   * person under one; the free-text offer is hidden. A stored config saved
   * before bets existed has no key, which reads as off (readTrackerConfig).
   */
  bets: boolean;
  /**
   * 'participant' turns `channels` into channel families, which each
   * participant copies and renames (Leads). A stored config without the key
   * reads from its scoreboard: cost per lead is 'participant'.
   */
  channelsBy: TrackerChannelsBy;
};

export type TrackerFieldKey = keyof TrackerFields;
export type RollupLabelKey = keyof TrackerConfig['rollupLabels'];

export type TrackerConfigError = {
  /** Dotted path to the field, e.g. "channels.0.stages.2.label". */
  path: string;
  message: string;
};

export const RIS_PRESET: TrackerConfig = {
  version: 1,
  channels: [
    {
      key: 'main',
      label: 'Outreach',
      flow: 'sequence',
      stages: [
        { key: 'to_contact', label: 'To contact' },
        { key: 'contacted', label: 'Contacted', role: 'contacted' },
        { key: 'interested', label: 'Replied: interested', role: 'replied' },
        { key: 'booked', label: 'Conversation booked', role: 'booked' },
        { key: 'held', label: 'Conversation held', role: 'held' },
        { key: 'decision', label: 'Decision pending', role: 'decision' },
        { key: 'won', label: 'Won', role: 'won' },
      ],
      exits: [
        { key: 'replied_no', label: 'Replied: no', role: 'replied_no' },
        { key: 'lost', label: 'Lost', role: 'lost' },
      ],
    },
  ],
  rollupLabels: {
    sends: 'Sends',
    replies: 'Replies',
    held: 'Conversations held',
    closes: 'Closes',
  },
  sequence: [
    { key: 'reconnection', name: 'Reconnection', gapDays: 0 },
    { key: 'introduction', name: 'Introduction', gapDays: 0 },
    { key: 'details', name: 'Details', gapDays: 7 },
    { key: 'questions', name: 'Questions', gapDays: 7 },
    { key: 'deadline', name: 'Deadline', gapDays: 7 },
  ],
  scoreboard: { kind: 'revenue' },
  fields: { offer: true, email: true, phone: true, segment: true, tier: true, note: true },
  bets: true,
  channelsBy: 'course',
};

function family(key: string, label: string, stages: string[]): TrackerChannel {
  return {
    key,
    label,
    flow: 'steps',
    stages: stages.map((stage) => ({ key: slugKey(stage, [], key), label: stage })),
    exits: [],
  };
}

/**
 * Danny's six channel families (15 Oct). Each participant copies one and
 * renames its stages in week two; these starting stages are our draft.
 * Danny's tests (16 Oct): every outreach family has a follow-up after its
 * first touch, never one touch and then waiting; Ads has one stage and
 * nothing else, as its spend is logged on its own. Danny (17 Oct): no
 * outreach family ends in "Conversation", which the destination row already
 * counts; Ads' stage is "Opted in", not "Clicked", as a click has no name
 * to add. The destination is each participant's own and is appended after
 * these stages, never stored here.
 */
export const LEADS_PRESET: TrackerConfig = {
  version: 1,
  channels: [
    family('direct_outreach', 'Direct outreach', ['Message sent', 'Followed up', 'Replied']),
    family('referral_activation', 'Referral activation', [
      'Ask made',
      'Followed up',
      'Introduction received',
    ]),
    family('networking', 'Networking', ['Met', 'Followed up']),
    family('speaking', 'Speaking and podcast guesting', [
      'Pitch sent',
      'Followed up',
      'Booked',
      'Appeared',
    ]),
    family('partner_outreach', 'Partner outreach', [
      'Partner contacted',
      'Followed up',
      'Partner agreed',
      'Promotion sent',
    ]),
    family('ads', 'Ads', ['Opted in']),
  ],
  rollupLabels: { ...RIS_PRESET.rollupLabels },
  sequence: [{ key: 'first_message', name: 'First message', gapDays: 0 }],
  scoreboard: { kind: 'cpl' },
  fields: { offer: false, email: true, phone: true, segment: false, tier: false, note: true },
  bets: false,
  channelsBy: 'participant',
};

export type TrackerPresetId = 'ris' | 'leads';

export type TrackerPreset = {
  config: TrackerConfig;
  /** The "Start from" button and the setup page: "RIS: revenue". */
  label: string;
  /** The short name in running text: "the RIS stages". */
  name: string;
};

export const TRACKER_PRESETS: Record<TrackerPresetId, TrackerPreset> = {
  ris: { config: RIS_PRESET, label: 'RIS: revenue', name: 'RIS' },
  leads: { config: LEADS_PRESET, label: 'Leads: cost per lead', name: 'Leads' },
};

/**
 * The config a course runs on: RIS_PRESET until a guide saves one, and a
 * stored config from before bets existed reads with bets off.
 */
export function readTrackerConfig(stored?: TrackerConfig): TrackerConfig {
  if (!stored) {
    return RIS_PRESET;
  }
  const cpl = stored.scoreboard.kind === 'cpl';
  return {
    ...stored,
    bets: stored.bets ?? false,
    channelsBy: stored.channelsBy ?? (cpl ? 'participant' : 'course'),
    scoreboard: cpl ? { kind: 'cpl' } : stored.scoreboard,
  };
}

/** Each participant maps their own channels from the course's families (Leads). */
export function participantChannels(config: Pick<TrackerConfig, 'channelsBy'>) {
  return config.channelsBy === 'participant';
}

/** JSON with object keys sorted, because a config read back from jsonb has its keys reordered. */
function stableJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableJson).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

function presetPart(config: TrackerConfig) {
  return stableJson([
    config.channels,
    config.rollupLabels,
    config.sequence,
    config.scoreboard,
    config.channelsBy,
  ]);
}

/**
 * The preset a config still matches on stages, labels, sequence and
 * scoreboard, if any. Fields and the bets switch are left out: they are
 * per-course choices on top of a preset, so changing them keeps the preset.
 */
export function matchingTrackerPreset(config: TrackerConfig): TrackerPresetId | undefined {
  return (Object.keys(TRACKER_PRESETS) as TrackerPresetId[]).find(
    (id) => presetPart(TRACKER_PRESETS[id].config) === presetPart(config),
  );
}

export const ROLLUP_LABEL_NAMES: Record<RollupLabelKey, string> = {
  sends: 'Sends',
  replies: 'Replies',
  held: 'Conversations held',
  closes: 'Closes',
};

/**
 * A new stable key made from a label: a slug, prefixed for steps stages, with
 * a numeric suffix where the slug is already taken.
 */
export function slugKey(label: string, taken: Iterable<string>, prefix?: string) {
  const used = new Set(taken);
  const slug =
    label
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'stage';
  const base = (prefix ? `${prefix}_${slug}` : slug)
    .slice(0, MAX_KEY_LENGTH - 4)
    .replace(/_+$/, '');
  if (!used.has(base)) {
    return base;
  }
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) {
      return candidate;
    }
  }
}

/** Every key in the config: channels, stages, exits and sequence steps. */
export function trackerConfigKeys(config: TrackerConfig) {
  const keys: string[] = [];
  for (const channel of config.channels) {
    keys.push(channel.key);
    for (const stage of [...channel.stages, ...channel.exits]) {
      keys.push(stage.key);
    }
  }
  for (const step of config.sequence) {
    keys.push(step.key);
  }
  return keys;
}

/** Stage and exit keys only, across every channel. */
export function trackerStageKeys(config: TrackerConfig) {
  return config.channels.flatMap((c) => [...c.stages, ...c.exits].map((s) => s.key));
}

export function sequenceChannel(config: TrackerConfig) {
  return config.channels.find((c) => c.flow === 'sequence');
}

export function isOpeningStep(step: TrackerSequenceStep) {
  return step.gapDays === 0;
}

/** Trims every label and drops an empty rollup label, so stored configs are tidy. */
export function normalizeTrackerConfig(config: TrackerConfig): TrackerConfig {
  function stage(s: TrackerStage): TrackerStage {
    const short = s.short?.trim();
    const next: TrackerStage = { key: s.key, label: s.label.trim() };
    if (short) {
      next.short = short;
    }
    if (s.role) {
      next.role = s.role;
    }
    return next;
  }
  return {
    ...config,
    channels: config.channels.map((c) => ({
      ...c,
      label: c.label.trim(),
      stages: c.stages.map(stage),
      exits: c.exits.map(stage),
    })),
    rollupLabels: {
      sends: config.rollupLabels.sends.trim(),
      replies: config.rollupLabels.replies.trim(),
      held: config.rollupLabels.held.trim(),
      closes: config.rollupLabels.closes.trim(),
    },
    sequence: config.sequence.map((s) => ({ ...s, name: s.name.trim() })),
    bets: !!config.bets,
    channelsBy: config.channelsBy === 'participant' ? 'participant' : 'course',
  };
}

function labelError(label: string, what: string, removable = true) {
  const trimmed = label.trim();
  if (!trimmed) {
    return removable ? `${what} is empty. Name it or remove it.` : `${what} is empty. Name it.`;
  }
  if (trimmed.length > MAX_LABEL_LENGTH) {
    return `${what} is longer than ${MAX_LABEL_LENGTH} characters. Shorten it.`;
  }
}

function sameRoles(actual: Array<StageRole | undefined>, expected: StageRole[]) {
  return actual.length === expected.length && actual.every((r, i) => r === expected[i]);
}

/**
 * Every rule a stored config must meet, as field-addressed errors. Empty
 * when the config is valid. Labels are judged trimmed.
 */
export function validateTrackerConfig(config: TrackerConfig): TrackerConfigError[] {
  const errors: TrackerConfigError[] = [];
  function add(path: string, message: string) {
    errors.push({ path, message });
  }

  const families = participantChannels(config);
  if (!config.channels.length) {
    add('channels', families ? 'Add a channel family.' : 'Add a channel.');
  }
  if (families && config.channels.length > MAX_FAMILIES) {
    add('channels', `Up to ${MAX_FAMILIES} channel families. Remove one.`);
  }
  if (!families && config.channels.length > MAX_CHANNELS) {
    add('channels', `Up to ${MAX_CHANNELS} channels. Remove one.`);
  }
  if (config.channels.filter((c) => c.flow === 'sequence').length > 1) {
    add('channels', 'Only one channel can use the outreach sequence.');
  }

  const seenKeys = new Set<string>();
  function checkKey(key: string, path: string) {
    if (!KEY_PATTERN.test(key)) {
      add(`${path}.key`, 'This item has an invalid key. Refresh the page and try again.');
    } else if (seenKeys.has(key)) {
      add(`${path}.key`, 'Two items share a key. Refresh the page and try again.');
    }
    seenKeys.add(key);
  }

  config.channels.forEach((channel, ci) => {
    const cpath = `channels.${ci}`;
    const name = channel.label.trim() || `Channel ${ci + 1}`;
    checkKey(channel.key, cpath);
    const channelLabelError = labelError(
      channel.label,
      `Channel ${ci + 1} name`,
      channel.flow === 'steps',
    );
    if (channelLabelError) {
      add(`${cpath}.label`, channelLabelError);
    }

    const seenLabels = new Set<string>();
    function checkStage(stage: TrackerStage, path: string, what: string) {
      checkKey(stage.key, path);
      const error = labelError(stage.label, what, channel.flow === 'steps');
      if (error) {
        add(`${path}.label`, error);
      } else {
        const label = stage.label.trim();
        const folded = label.toLowerCase();
        if (seenLabels.has(folded)) {
          add(
            `${path}.label`,
            `“${label}” appears twice in ${name}. Stage names must differ within a channel.`,
          );
        }
        seenLabels.add(folded);
      }
      if (stage.short !== undefined) {
        const short = stage.short.trim();
        if (short.length > MAX_SHORT_LENGTH) {
          add(
            `${path}.short`,
            `${what} rollup label is longer than ${MAX_SHORT_LENGTH} characters. Shorten it.`,
          );
        }
      }
    }
    channel.stages.forEach((stage, si) => {
      checkStage(stage, `${cpath}.stages.${si}`, `Stage ${si + 1}`);
    });
    channel.exits.forEach((exit, ei) => {
      checkStage(exit, `${cpath}.exits.${ei}`, `Side exit ${ei + 1}`);
    });

    if (families) {
      if (channel.flow !== 'steps') {
        add(`${cpath}.stages`, `${name} must be a list of steps.`);
      }
      if (!channel.stages.length) {
        add(`${cpath}.stages`, `${name} needs at least one stage.`);
      }
      if (channel.stages.length > MAX_FUNNEL_STAGES) {
        add(`${cpath}.stages`, `${name} has more than ${MAX_FUNNEL_STAGES} stages. Remove one.`);
      }
      if (channel.stages.some((s) => s.role)) {
        add(`${cpath}.stages`, `${name} stages can’t carry a role.`);
      }
      if (channel.exits.length) {
        add(`${cpath}.exits`, `${name} cannot have side exits.`);
      }
    } else if (channel.flow === 'sequence') {
      const roles = channel.stages.map((s) => s.role);
      const ladder = roles[0] === undefined ? roles.slice(1) : roles;
      if (!sameRoles(ladder, SEQUENCE_LADDER_ROLES)) {
        add(
          `${cpath}.stages`,
          `${name} must keep its stages in order: first touch, a reply, booking, conversation held, decision, close.`,
        );
      }
      const exitRoles = channel.exits.map((s) => s.role);
      if (
        exitRoles.length !== SEQUENCE_EXIT_ROLES.length ||
        SEQUENCE_EXIT_ROLES.some((role) => !exitRoles.includes(role))
      ) {
        add(`${cpath}.exits`, `${name} must keep its two side exits: replied no, and lost.`);
      }
    } else {
      if (!channel.stages.length) {
        add(`${cpath}.stages`, `${name} needs at least one stage.`);
      }
      if (channel.stages.length > MAX_STEPS_STAGES) {
        add(`${cpath}.stages`, `${name} has more than ${MAX_STEPS_STAGES} stages. Remove one.`);
      }
      if (channel.stages.some((s) => s.role && s.role !== 'booked')) {
        add(`${cpath}.stages`, `${name} stages can only be marked as a booking.`);
      }
      if (channel.stages.filter((s) => s.role === 'booked').length > 1) {
        add(`${cpath}.stages`, `Only one stage in ${name} can count as a booking.`);
      }
      if (channel.exits.length) {
        add(`${cpath}.exits`, `${name} cannot have side exits.`);
      }
    }
  });

  (Object.keys(ROLLUP_LABEL_NAMES) as RollupLabelKey[]).forEach((key) => {
    const error = labelError(config.rollupLabels[key], `The ${ROLLUP_LABEL_NAMES[key]} label`);
    if (error) {
      add(`rollupLabels.${key}`, error);
    }
  });

  if (!config.sequence.length) {
    add('sequence', 'Add at least one step to the touch sequence.');
  }
  if (config.sequence.length > MAX_SEQUENCE_STEPS) {
    add('sequence', `Up to ${MAX_SEQUENCE_STEPS} steps in the touch sequence. Remove one.`);
  }
  const openingCount = config.sequence.filter(isOpeningStep).length;
  const firstLater = config.sequence.findIndex((s) => !isOpeningStep(s));
  if (config.sequence.length && (openingCount < 1 || openingCount > MAX_OPENING_STEPS)) {
    add(
      'sequence',
      `The touch sequence needs 1 to ${MAX_OPENING_STEPS} opening touches (0 days). Set one or remove one.`,
    );
  }
  const seenStepNames = new Set<string>();
  config.sequence.forEach((step, i) => {
    const path = `sequence.${i}`;
    checkKey(step.key, path);
    const error = labelError(step.name, `Step ${i + 1}`);
    if (error) {
      add(`${path}.name`, error);
    } else {
      const folded = step.name.trim().toLowerCase();
      if (seenStepNames.has(folded)) {
        add(
          `${path}.name`,
          `“${step.name.trim()}” appears twice in the touch sequence. Step names must differ.`,
        );
      }
      seenStepNames.add(folded);
    }
    if (isOpeningStep(step)) {
      if (firstLater >= 0 && i > firstLater) {
        add(
          `${path}.gapDays`,
          `Step ${i + 1} is an opening touch after a later step. Move it up or give it 1 to ${MAX_GAP_DAYS} days.`,
        );
      }
    } else if (!Number.isInteger(step.gapDays) || step.gapDays < 1 || step.gapDays > MAX_GAP_DAYS) {
      add(`${path}.gapDays`, `Step ${i + 1} needs 0, or 1 to ${MAX_GAP_DAYS} days.`);
    }
  });

  if (config.scoreboard.kind === 'revenue') {
    if (!sequenceChannel(config)) {
      add(
        'scoreboard.kind',
        'Revenue needs the outreach sequence. Start from RIS: revenue, or from Leads: cost per lead.',
      );
    }
  } else if (!families) {
    add('scoreboard.kind', 'Cost per lead needs channel families. Start from Leads.');
  }

  return errors;
}

/**
 * The next step of the touch sequence after the one last sent. A person gets
 * one opening touch (Reconnection for past contacts, Introduction for new
 * ones), so every opening step leads to the first later step.
 */
export function nextSequenceStep(
  config: TrackerConfig,
  lastStepKey: string,
  lastOn: string,
): { key: string; name: string; on: string } | undefined {
  const index = config.sequence.findIndex((s) => s.key === lastStepKey);
  if (index < 0) {
    return;
  }
  const next = isOpeningStep(config.sequence[index])
    ? config.sequence.find((s) => !isOpeningStep(s))
    : config.sequence[index + 1];
  if (!next) {
    return;
  }
  return { key: next.key, name: next.name, on: addDays(lastOn, next.gapDays) };
}

/** "Reconnection or Introduction → Details +7 days → Deadline +7 days". */
export function sequencePreview(sequence: TrackerSequenceStep[]) {
  const openings = sequence.filter(isOpeningStep).map((s) => s.name.trim() || 'Untitled');
  const later = sequence
    .filter((s) => !isOpeningStep(s))
    .map((s) => `${s.name.trim() || 'Untitled'} +${s.gapDays} ${s.gapDays === 1 ? 'day' : 'days'}`);
  return [openings.join(' or '), ...later].filter(Boolean).join(' → ');
}

export type TrackerKeyUsage = {
  /** Logged step touches per stage or exit key. */
  stepsByStage: Record<string, number>;
  /** Logged touches per touch-sequence step key. */
  stepsBySequence: Record<string, number>;
  /** People currently on each stage or exit key. */
  peopleByStage: Record<string, number>;
  /** People in each channel. */
  peopleByChannel: Record<string, number>;
};

function plural(n: number, one: string, many: string) {
  return n === 1 ? `1 ${one}` : `${n} ${many}`;
}

/** "37 steps are logged at “Accepted”. Rename it instead." */
export function loggedStepsMessage(count: number, label: string) {
  return `${plural(count, 'step is', 'steps are')} logged at “${label}”. Rename it instead.`;
}

/** Why a stage or exit cannot be removed, or undefined when nothing uses it. */
export function stageUsageMessage(key: string, label: string, usage: TrackerKeyUsage) {
  const steps = usage.stepsByStage[key] ?? 0;
  const people = usage.peopleByStage[key] ?? 0;
  if (steps > 0) {
    return loggedStepsMessage(steps, label);
  }
  if (people > 0) {
    return `${plural(people, 'person is', 'people are')} on “${label}”. Rename it instead.`;
  }
}

/** Why a touch-sequence step cannot be removed, or undefined when no touch uses it. */
export function sequenceUsageMessage(key: string, label: string, usage: TrackerKeyUsage) {
  const touches = usage.stepsBySequence[key] ?? 0;
  if (touches > 0) {
    return `${plural(touches, 'touch is', 'touches are')} logged at “${label}”. Rename it instead.`;
  }
}

function stageLabels(config: TrackerConfig) {
  const labels = new Map<string, string>();
  for (const channel of config.channels) {
    labels.set(channel.key, channel.label);
    for (const stage of [...channel.stages, ...channel.exits]) {
      labels.set(stage.key, stage.label);
    }
  }
  for (const step of config.sequence) {
    labels.set(step.key, step.name);
  }
  return labels;
}

/**
 * Stages, exits, sequence steps and channels that `next` drops while
 * participants still use them. Logged touches and people are keyed by these
 * keys, so dropping one would orphan them; the guide renames instead.
 */
export function trackerRemovalErrors(
  previous: TrackerConfig,
  next: TrackerConfig,
  usage: TrackerKeyUsage,
): TrackerConfigError[] {
  // Each kind is checked against its own kind: a stage key reused as a
  // sequence-step key (or the reverse) does not keep the old one.
  const keptStages = new Set(trackerStageKeys(next));
  const keptSteps = new Set(next.sequence.map((s) => s.key));
  const keptChannels = new Set(next.channels.map((c) => c.key));
  const labels = stageLabels(previous);
  const errors: TrackerConfigError[] = [];
  const stageKeys = new Set([
    ...Object.keys(usage.stepsByStage),
    ...Object.keys(usage.peopleByStage),
  ]);
  for (const key of stageKeys) {
    if (keptStages.has(key)) {
      continue;
    }
    const message = stageUsageMessage(key, labels.get(key) ?? key, usage);
    if (message) {
      errors.push({ path: `removed.${key}`, message });
    }
  }
  for (const key of Object.keys(usage.stepsBySequence)) {
    if (keptSteps.has(key)) {
      continue;
    }
    const message = sequenceUsageMessage(key, labels.get(key) ?? key, usage);
    if (message) {
      errors.push({ path: `removed.${key}`, message });
    }
  }
  for (const [key, people] of Object.entries(usage.peopleByChannel)) {
    if (people > 0 && !keptChannels.has(key)) {
      errors.push({
        path: `removed.${key}`,
        message: `${plural(people, 'person is', 'people are')} in “${
          labels.get(key) ?? key
        }”. Rename the channel instead.`,
      });
    }
  }
  return errors;
}
